import apiClient from './client'
import type {
  AIUsageLimit,
  GroupJournal,
  GroupSlot,
  RollCallRequest,
  RollCallResult,
  SessionBoard,
  TrainingSession,
  AttendanceStatus,
} from '@shared/types/api'

/** Открыть занятие группы на дату. Занятие создаётся при первом обращении. */
export interface ResolveSessionRequest {
  assistant_id: string
  group_id: string
  /** YYYY-MM-DD, по умолчанию сегодня */
  date?: string
  /** HH:MM, нужно только если у группы в этот день несколько тренировок */
  start_time?: string
}

/**
 * Голосовая перекличка: расшифрованная фраза тренера.
 * Пользователь берётся из авторизации, user_id бэкенд не принимает.
 */
export interface VoiceRollCallRequest {
  assistant_id: string
  text: string
}

/** Ответ на голосовую перекличку: реплика ассистента и обновлённая доска. */
export interface VoiceRollCallResponse {
  reply: string
  board: SessionBoard
  /** Квота AI-запросов после этого разбора (голос расходует её, как чат) */
  ai_usage?: AIUsageLimit
}

/** Перекличка текстом с экрана занятия: занятие задаёт экран, фразу разбирает сервер. */
export interface RollCallPreviewRequest {
  group_id: string
  /** YYYY-MM-DD */
  date: string
  /** HH:MM */
  start_time?: string
  text: string
}

/** Строка состава: отметка до разбора и после записи. Пустой статус - без отметки. */
export interface RollCallPreviewRowDTO {
  athlete_id: string
  name: string
  before?: AttendanceStatus
  before_notes?: string
  after?: AttendanceStatus
  after_notes?: string
  changed: boolean
}

/**
 * Разобранная перекличка. Короткая (конкретные дети) уже записана -
 * recorded: true. Массовая ничего не пишет: запись - подтверждение
 * отложенного действия pending_action_id (POST /ai-actions/{id}/confirm).
 * Запрос Штаба разбор не тратит.
 */
export interface RollCallPreviewDTO {
  group_id: string
  group_name: string
  understood: boolean
  rows: RollCallPreviewRowDTO[] | null
  changes: number
  not_found: string[] | null
  ambiguous: { query: string; candidates: string[] | null }[] | null
  mass?: 'rest_absent' | 'all_except' | 'all_present' | ''
  blocked: boolean
  recorded?: boolean
  pending_action_id?: string
}

export const trainingSessionsApi = {
  // POST /api/v1/training-sessions/resolve
  resolve: async (data: ResolveSessionRequest): Promise<SessionBoard> => {
    const response = await apiClient.post<SessionBoard>('/training-sessions/resolve', data)
    return response.data
  },

  // GET /api/v1/training-sessions/board - посмотреть занятие, ничего не создавая.
  // Если занятия ещё нет в базе, session_exists = false и отметок нет.
  peek: async (groupId: string, date: string, startTime?: string): Promise<SessionBoard> => {
    const response = await apiClient.get<SessionBoard>('/training-sessions/board', {
      params: { group_id: groupId, date, start_time: startTime || undefined },
    })
    return response.data
  },

  // GET /api/v1/training-sessions/{id}
  getBoard: async (sessionId: string): Promise<SessionBoard> => {
    const response = await apiClient.get<SessionBoard>(`/training-sessions/${sessionId}`)
    return response.data
  },

  // POST /api/v1/training-sessions/{id}/marks — отметка одним тапом
  mark: async (
    sessionId: string,
    athleteId: string,
    status: AttendanceStatus,
    notes?: string
  ): Promise<SessionBoard> => {
    const response = await apiClient.post<SessionBoard>(`/training-sessions/${sessionId}/marks`, {
      athlete_id: athleteId,
      status,
      notes,
    })
    return response.data
  },

  // DELETE /api/v1/training-sessions/{id}/marks/{athleteId} — снять отметку
  unmark: async (sessionId: string, athleteId: string): Promise<SessionBoard> => {
    const response = await apiClient.delete<SessionBoard>(
      `/training-sessions/${sessionId}/marks/${athleteId}`
    )
    return response.data
  },

  // POST /api/v1/training-sessions/roll-call — перекличка пачкой имён
  rollCall: async (data: RollCallRequest): Promise<RollCallResult> => {
    const response = await apiClient.post<RollCallResult>('/training-sessions/roll-call', data)
    return response.data
  },

  // POST /api/v1/training-sessions/roll-call/preview - разбор фразы переклички
  // без записи (экран занятия нового интерфейса). Расходует квоту AI-запросов.
  previewRollCallText: async (data: RollCallPreviewRequest): Promise<RollCallPreviewDTO> => {
    const response = await apiClient.post<RollCallPreviewDTO>('/training-sessions/roll-call/preview', data)
    return response.data
  },

  // POST /api/v1/training-sessions/{id}/voice — разбор произнесённой фразы.
  // Занятие уже известно, поэтому группу и дату по фразе угадывать не нужно.
  // skipAuthRedirect: на 401 экран переклички сам просит войти заново, а не
  // улетает на /login посреди записи.
  voiceRollCall: async (
    sessionId: string,
    data: VoiceRollCallRequest
  ): Promise<VoiceRollCallResponse> => {
    const response = await apiClient.post<VoiceRollCallResponse>(
      `/training-sessions/${sessionId}/voice`,
      data,
      { skipAuthRedirect: true }
    )
    return response.data
  },

  // POST /api/v1/training-sessions/{id}/status — «тренировки не было» и обратно
  setStatus: async (sessionId: string, status: 'held' | 'cancelled'): Promise<SessionBoard> => {
    const response = await apiClient.post<SessionBoard>(`/training-sessions/${sessionId}/status`, {
      status,
    })
    return response.data
  },

  // GET /api/v1/training-sessions/current — какие группы тренируются сейчас
  current: async (assistantId: string, at?: string): Promise<GroupSlot[]> => {
    const response = await apiClient.get<GroupSlot[]>('/training-sessions/current', {
      params: { assistant_id: assistantId, at },
    })
    return response.data
  },

  // GET /api/v1/groups/{groupId}/attendance/journal — журнал за период
  journal: async (groupId: string, from: string, to: string): Promise<GroupJournal> => {
    const response = await apiClient.get<GroupJournal>(
      `/groups/${groupId}/attendance/journal`,
      { params: { from, to } }
    )
    return response.data
  },

  // GET /api/v1/groups/{groupId}/training-sessions — занятия группы за период
  listByGroup: async (groupId: string, from: string, to: string): Promise<TrainingSession[]> => {
    const response = await apiClient.get<TrainingSession[]>(
      `/groups/${groupId}/training-sessions`,
      { params: { from, to } }
    )
    return response.data
  },
}
